// shared/product-facts.js — the figures the product enforces, stated once.
//
// Every one of these was previously written out as prose on 53 marketing pages
// AND declared as a constant somewhere in server/. Keeping the two in agreement
// was a review job: scripts/check-marketing-claims.mjs had to read the constants
// back out of source with a regex, and it still only caught a page whose figure
// was WRONG — never one that was merely stale in a way the regex didn't model.
// Six pages undersold the free allowance that way (#217).
//
// Now the constant lives here, the server imports it, and a marketing page
// writes `<!-- @fact:trial-days -->` rather than a number.
// scripts/build-web.mjs substitutes the value at build time and FAILS on an
// unknown name, so a fact cannot be misquoted and a renamed fact cannot go
// unnoticed.
//
// Pure: imports nothing but shared/currencies.js. Both runtimes, and the build.
import { CURRENCIES } from "./currencies.js";
import {
  STATUTORY_BAND_LOW_PENCE,
  STATUTORY_BAND_HIGH_PENCE,
  STATUTORY_COMPENSATION_PENCE
} from "./statutory-interest.js";

// Sendinvo's own subscription: £24.99 a month, on the web and in the App
// Store alike (decided by Andy on 20 October 2026). One plan, every feature:
// there are no tiers and nothing is gated, and since 13 October 2026 it is the
// whole price for the whole team (ADR-042): the owner's subscription is the
// one paid place, and everyone they invite, in any role, is included.
//
// The price INCLUDES VAT. Unobtrusive Ltd is VAT registered, and £24.99 is
// everything a customer pays: the Stripe Price is tax-inclusive
// (`tax_behavior: inclusive`, so Stripe works the VAT out inside it rather
// than adding it on top), and App Store prices always include VAT. That is
// why COPY.md still keeps VAT out of our pricing copy: there is nothing to
// add. The one place it is said aloud is beside a competitor quoting its
// price before VAT (marketing/compare/), so a reader compares like with like.
//
// In pence, as every other sum here is, so no float decides a penny. The web
// matches the App Store to the penny: £24.99 is an App Store price point, so
// there is one figure, and nothing quotes two (it was £20 on the web and
// £19.99 in the App Store, which had no £20 point, until 20 October 2026).
export const MONTHLY_PRICE_PENCE = 2499;

// The same price in pounds, for the API's `priceGbp` and `appStorePriceGbp`
// (a number the app formats in its own locale) and nothing that does sums.
export const MONTHLY_PRICE_GBP = MONTHLY_PRICE_PENCE / 100;

// The plan in the App Store, `sendinvo.solo.monthly`: the same price as the
// web, by construction. App Store Connect holds the figure itself, set by
// choosing the UK price point that equals this (08-listing.md); the
// subscribe sheet shows Apple's own displayPrice. One plan, the whole team,
// as on the web (ADR-042).
export const APP_STORE_PRICE_PENCE = MONTHLY_PRICE_PENCE;
export const APP_STORE_PRICE_GBP = APP_STORE_PRICE_PENCE / 100;

// Every new account starts with this many days of every feature, no card and
// no invoice limit. Replaced the lifetime free-invoice allowance (#378), whose
// constant and markers went on 14 October 2026; scripts/check-marketing-claims.mjs
// fails a page that promises free invoices or a free plan again.
export const TRIAL_DAYS = 30;

// Stripe Pay by Bank ("Instant bank payment") is GBP-only and capped.
export const PAY_BY_BANK_CURRENCY = "GBP";
export const PAY_BY_BANK_MAX_PENCE = 1000000; // £10,000

// There are deliberately NO constants for Stripe's own fees here, and no
// marker a page could quote one with. We tried it (#421) and took it out the
// same day: a figure we cannot verify from the code is a figure we cannot
// stand behind, and it is somebody else's price to change. The copy says a
// processing fee applies and links to stripe.com/gb/pricing, which is always
// current because Stripe maintains it. See ADR-018 before adding one back.

// Files that ride along with an invoice email — company defaults plus
// per-invoice extras. The budget is the person's FILES; the document's own
// PDF rides on top of it, uncounted (8 October 2026: nobody can know how big
// their PDF is, so a limit that counted it read as a figure they couldn't
// check). The arithmetic for why that is still safe is beside
// `fitsEmailAttachmentBudget` in shared/email-attachments.js.
export const EMAIL_ATTACHMENT_BUDGET_BYTES = 10 * 1024 * 1024;
export const MAX_EMAIL_ATTACHMENTS = 10;

// How much a whole company may store in per-invoice attachments. The per-email
// budget alone does not bound this: drafts are deliberately ungated, so
// (drafts × 9MB) is an attacker-chosen number against a shared Postgres. A
// generous ceiling for real use — 200MB is ~1,000 typical PDFs — that still
// stops one account filling the instance for everyone.
export const EMAIL_ATTACHMENT_COMPANY_BUDGET_BYTES = 200 * 1024 * 1024;

// How long "Export everything"'s emailed link works, and so how long the file
// is kept (feature sketch 6, server/company-export.js): long enough to
// download it after a week away, short enough that an old email forwarded on,
// or read on a lost phone, can't be used to download every client's details.
export const EXPORT_LINK_DAYS = 7;

// Where the Sendinvo app can be had, as ONE sentence every marketing page
// quotes rather than words of its own (20 October 2026). Prose never names a
// platform: a page says "the Sendinvo app", and wherever availability matters
// (a comparison table, an FAQ, a feature only the app has) it writes
// `<!-- @fact:app-availability -->`, so no page implies anyone can use the app
// before they can. scripts/check-marketing-claims.mjs fails a page that types
// "iPhone", "iPad", "iOS", "Android" or "mobile app" outside a marker.
//
// Launched (the launch kit, merged the day App Review approved the app):
// "available on the App Store" is the wording Apple's Marketing Guidelines
// call correct, never "in". Android's turn is the same edit, and so is the
// day it reaches another country's storefront. `npm run build:ios-bundle`
// after any change (FACTS rides in the app's bundle, so check:ios-bundle
// fails until it is rebuilt; no Swift reads it). A sentence that stands on
// its own, because it opens an FAQ answer and fills a table cell.
export const APP_AVAILABILITY =
  "The Sendinvo app is available on the App Store for iPhone and iPad, and there’s no Android app yet.";

// The app's App Store listing (Apple ID 6814212181, the UK storefront). The id
// form needs no name in the path: Apple redirects it to the listing under
// whatever the app is called that day, so renaming it in App Store Connect
// breaks no link. Every badge links here (`<!-- @fact:app-store-url -->`),
// and the Smart App Banner names the id (`<!-- @fact:app-store-id -->`).
export const APP_STORE_ID = "6814212181";
export const APP_STORE_URL = `https://apps.apple.com/gb/app/id${APP_STORE_ID}`;

// Derived, so it can never disagree with the list routes actually accept.
export const CURRENCY_COUNT = CURRENCIES.length;

const NUMBER_WORDS = [
  "zero", "one", "two", "three", "four", "five", "six", "seven", "eight", "nine",
  "ten", "eleven", "twelve", "thirteen", "fourteen", "fifteen", "sixteen",
  "seventeen", "eighteen", "nineteen", "twenty"
];

// Sentence-case prose spells out small numbers; tables and headlines use
// digits. Both forms come from the same constant so they cannot diverge.
export function inWords(n) {
  return NUMBER_WORDS[n] ?? String(n);
}

function capitalise(word) {
  return word.charAt(0).toUpperCase() + word.slice(1);
}

function pounds(pence) {
  return `£${(pence / 100).toLocaleString("en-GB")}`;
}

// A price to the penny: "£24.99", never "£24.990" or "£25". Whole pounds
// still show their pence ("£20.00"), because a price that is sometimes "£20"
// and sometimes "£24.99" reads as two kinds of figure.
function price(pence) {
  return `£${pricePounds(pence)}`;
}

// The bare decimal, with no symbol: "24.99". What JSON-LD's Offer.price wants.
function pricePounds(pence) {
  if (!Number.isInteger(pence) || pence < 0) throw new Error(`product-facts: a price is whole pence, not ${pence}`);
  return `${Math.floor(pence / 100)}.${String(pence % 100).padStart(2, "0")}`;
}

// The marker vocabulary. A marketing page writes `<!-- @fact:NAME -->` and the
// build substitutes the string. Add a name here before using it in a page —
// the build fails on anything it doesn't recognise.
export const FACTS = {
  // "£24.99". Including VAT, which is never added on top (see above).
  "monthly-price": price(MONTHLY_PRICE_PENCE),
  // "24.99": the figure without its symbol.
  "monthly-price-number": pricePounds(MONTHLY_PRICE_PENCE),
  "trial-days": String(TRIAL_DAYS),
  // "£24.99", the same as the web's, by construction.
  "app-store-price": price(APP_STORE_PRICE_PENCE),
  // JSON-LD Offer.price wants a bare decimal, no symbol: "24.99".
  "monthly-price-decimal": pricePounds(MONTHLY_PRICE_PENCE),
  "pay-by-bank-ceiling": pounds(PAY_BY_BANK_MAX_PENCE),
  // The whole phrase, so two pages can't split it differently.
  "email-attachment-budget": `${EMAIL_ATTACHMENT_BUDGET_BYTES / (1024 * 1024)} MB`,
  "currency-count": String(CURRENCY_COUNT),
  "currency-count-word": inWords(CURRENCY_COUNT),
  // Sentence-initial forms. A heading that opens "Ten currencies…" needs the
  // capital to come from here too, or it gets typed back in by hand.
  "currency-count-word-cap": capitalise(inWords(CURRENCY_COUNT)),
  // Late Payment of Commercial Debts (Interest) Act 1998 bands. UK law — these
  // share a value with the Pay by Bank ceiling today and must NOT be bound to it.
  "statutory-band-low": pounds(STATUTORY_BAND_LOW_PENCE),
  "statutory-band-high": pounds(STATUTORY_BAND_HIGH_PENCE),
  // The Act's fixed compensation per band (£40, £70, £100), which the late
  // payment terms an invoice can state quote for its own amount (#934).
  // Checked against GOV.UK on 7 October 2026. UK law, like the bands above.
  "statutory-compensation-low": pounds(STATUTORY_COMPENSATION_PENCE.under),
  "statutory-compensation-mid": pounds(STATUTORY_COMPENSATION_PENCE.mid),
  "statutory-compensation-high": pounds(STATUTORY_COMPENSATION_PENCE.over),
  "export-link-days": String(EXPORT_LINK_DAYS),
  // A whole sentence, ending in its full stop: see APP_AVAILABILITY.
  "app-availability": APP_AVAILABILITY,
  // Where every App Store badge goes, and the id the Smart App Banner names.
  "app-store-url": APP_STORE_URL,
  "app-store-id": APP_STORE_ID
};
